import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { formatFullDate, getAllGames, getGameById, getGameHighlights, getTeamById, stageLabel } from "@/utils/nba";
import TeamBadge from "@/components/base/TeamBadge";

export default function Replay() {
  const { gameId } = useParams();
  const game = getGameById(gameId);
  const [active, setActive] = useState(0);

  const home = getTeamById(game?.homeTeamId);
  const away = getTeamById(game?.awayTeamId);
  const highlights = game ? getGameHighlights(game) : [];

  useSeo({
    title: game && home && away
      ? `${away.fullName} vs ${home.fullName} 回放集锦 - NBA直播吧`
      : "赛事回放集锦 - NBA直播吧",
    description: game && home && away
      ? `观看 ${away.fullName} 对阵 ${home.fullName}（${formatFullDate(game.date)}，${stageLabel(game.stage)}）的比赛录像回放与关键事件集锦，最终比分 ${game.awayScore}-${game.homeScore}。`
      : "NBA 赛事回放与集锦页面。",
    keywords: "NBA回放,NBA录像,NBA集锦,NBA比赛回放,NBA直播吧",
  });

  if (!game || !home || !away) {
    return (
      <div className="mx-auto flex max-w-page flex-col items-center justify-center px-4 py-24 text-center">
        <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-film-line text-2xl" />
        </span>
        <h1 className="font-heading text-xl font-bold text-foreground-950">未找到该场回放</h1>
        <p className="mt-2 text-sm text-foreground-500">回放可能已下架，去看看其他精彩赛事回放吧。</p>
        <Link to="/replays" className="mt-6 rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 cursor-pointer">
          浏览回放集锦
        </Link>
      </div>
    );
  }

  const related = getAllGames().filter((item) => item.id !== game.id && item.status === "final").slice(0, 4);

  return (
    <div className="mx-auto max-w-page px-4 py-8 md:px-6 md:py-10">
      <nav className="mb-5 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="hover:text-primary-700 cursor-pointer">首页</Link>
        <i className="ri-arrow-right-s-line" />
        <Link to="/replays" className="hover:text-primary-700 cursor-pointer">回放集锦</Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-900 font-medium">{away.abbreviation} vs {home.abbreviation}</span>
      </nav>

      <div className="mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
          {away.fullName} vs {home.fullName} 全场回放
        </h1>
        <p className="mt-2 text-sm text-foreground-500">
          {stageLabel(game.stage)} · {formatFullDate(game.date)} · {game.arena} · 最终比分 {game.awayScore} - {game.homeScore}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-foreground-950">
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
              <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-background-50/15 text-background-50 backdrop-blur">
                <i className="ri-play-fill text-3xl" />
              </span>
              <p className="font-heading text-lg font-semibold text-background-50">回放播放器（界面演示）</p>
              <p className="mt-1 max-w-md px-4 text-xs text-background-100/70">
                本站未接入真实回放资源，播放器区域仅用于界面演示。点击右侧回放索引可查看对应阶段的关键事件说明。
              </p>
            </div>
            <div className="absolute bottom-4 left-4 right-4">
              <div className="h-1 w-full rounded-full bg-background-50/25">
                <div className="h-1 rounded-full bg-accent-500" style={{ width: `${((active + 1) / Math.max(highlights.length, 1)) * 100}%` }} />
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <h2 className="mb-3 font-heading text-lg font-semibold text-foreground-950">赛事简述</h2>
            <p className="text-sm leading-relaxed text-foreground-700">
              {away.fullName}客场挑战{home.fullName}，双方在攻防两端均奉献了高强度对抗。最终{home.fullName}以 {game.homeScore} - {game.awayScore} 战胜{away.fullName}。
              以下是本场比赛的关键事件回顾，点击右侧时间点可查看对应阶段说明。
            </p>
          </div>
        </div>

        {/* 回放索引 */}
        <div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <h2 className="mb-4 font-heading text-base font-semibold text-foreground-950">回放索引</h2>
            <div className="space-y-2">
              {highlights.map((item, index) => (
                <button
                  key={`${item.time}-${index}`}
                  type="button"
                  onClick={() => setActive(index)}
                  className={`w-full rounded-md border p-3 text-left transition-colors cursor-pointer ${
                    active === index ? "border-primary-400 bg-primary-50" : "border-background-200 hover:bg-background-100"
                  }`}
                >
                  <div className="flex items-center justify-between text-xs text-foreground-500">
                    <span>{item.time}</span>
                    <span className="rounded bg-secondary-100 px-1.5 py-0.5 text-secondary-900">{item.label}</span>
                  </div>
                  <p className="mt-1.5 text-sm text-foreground-900">{item.description}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <section className="mt-12">
        <h2 className="mb-5 font-heading text-xl md:text-2xl font-bold text-foreground-950">相关推荐回放</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {related.map((item) => {
            const rh = getTeamById(item.homeTeamId);
            const ra = getTeamById(item.awayTeamId);
            if (!rh || !ra) return null;
            return (
              <Link
                key={item.id}
                to={`/replay/${item.id}`}
                className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-all hover:border-primary-300 cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <TeamBadge team={ra} size="sm" />
                  <TeamBadge team={rh} size="sm" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground-950">{ra.fullName} vs {rh.fullName}</p>
                  <p className="text-xs text-foreground-500">{formatFullDate(item.date)} · {item.awayScore} - {item.homeScore}</p>
                </div>
                <i className="ri-play-circle-line text-xl text-primary-600" />
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}